/**
 * BookingDetail — canonical booking detail view (Iter 86, Sec 32/37/40).
 *
 * Anyone with visibility into the booking (customer, artist, assigned
 * manager, agency, admin) can open this page. It shows the header +
 * embedded PaymentTimeline widget with inline "Mark Paid" gated to
 * admin/manager only (canEdit prop on the widget).
 */
import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import Nav from "../components/Nav";
import api, { API, formatApiError as fmt } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import MediaUploader from "../components/MediaUploader";
import TechRiderLink from "../components/TechRiderLink";
import { PaymentTimeline } from "../components/PaymentPayoutWidgets";
import { canViewArtistContact, getBookingAttention, getBookingDetailVisibility, getBookingProgress, getPaymentAttemptDisplayState, getPaymentAttemptTimestamp, shouldShowCustomerContactCard, summarizeBookingPayment } from "./bookingDetailSummary";
import { formatPaymentDueLabel, formatPaymentMilestoneLabel } from "../lib/paymentTimeline";

const money = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const STATUS_TINT = {
  confirmed: "green", completed: "green", reviewed: "green",
  pending_artist: "gold", pending_payment: "gold", started: "gold",
  rejected: "red", cancelled: "red", auto_expired: "red",
};

export default function BookingDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  const [booking, setBooking] = useState(null);
  const [artistMeta, setArtistMeta] = useState(null);
  const [artistUser, setArtistUser] = useState(null);
  const [customerUser, setCustomerUser] = useState(null);
  const [eventMedia, setEventMedia] = useState({ details: "", media: [] });
  const [eventDetailsDraft, setEventDetailsDraft] = useState("");
  const [eventDetailsBusy, setEventDetailsBusy] = useState(false);
  const [eventMediaTitle, setEventMediaTitle] = useState("");
  const [replacingMediaId, setReplacingMediaId] = useState("");
  const [financialSummary, setFinancialSummary] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  const canEditPayments = ["admin", "manager"].includes(user?.role);
  const canAccessEventMedia = ["admin", "customer", "corporate", "artist", "manager"].includes(user?.role);

  const loadEventMedia = async () => {
    try {
      const response = await api.get(`/bookings/${id}/event-media`);
      const data = response.data || {};
      setEventMedia({ details: data.details || "", media: data.media || [] });
      setEventDetailsDraft(data.details || "");
    } catch (error) {
      setEventMedia({ details: "", media: [] });
      toast(`Could not load event information or media: ${fmt(error)}`, "error");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bookings/${id}`);
      const bookingDoc = r.data.booking || r.data;
      setBooking(bookingDoc);
      setArtistMeta(r.data.artist_profile || r.data.artist || null);
      setArtistUser(r.data.artist || null);
      setCustomerUser(r.data.customer || null);
      setContactUnlocked(Boolean(r.data.contact_unlocked));
      if (canAccessEventMedia) await loadEventMedia();
      setFinancialSummary(bookingDoc.financial_summary || null);
      try {
        const summary = await api.get(`/bookings/${id}/financial-summary`);
        setFinancialSummary(summary.data?.financial_summary || bookingDoc.financial_summary || null);
      } catch { /* summary is optional */ }
      if (["admin", "manager", "agency", "artist"].includes(user?.role)) {
        try {
          const p = await api.get(`/bookings/${id}/payouts`);
          setPayouts(p.data.items || []);
        } catch { setPayouts([]); }
      } else {
        setPayouts([]);
      }
      if (["admin", "manager", "agency", "customer", "corporate"].includes(user?.role)) {
        try {
          const s = await api.get(`/bookings/${id}/schedule`);
          setSchedule(s.data || null);
        } catch { setSchedule(null); }
      } else {
        setSchedule(null);
      }
    } catch (e) { toast(fmt(e), "error"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (loading) {
    return (
      <div>
        <Nav />
        <div className="pad-24" data-testid="bd-loading">Loading booking…</div>
      </div>
    );
  }
  if (!booking) {
    return (
      <div>
        <Nav />
        <div className="pad-24" data-testid="bd-not-found">
          Booking not found or you don't have access.
          <div className="mt-16">
            <Link to="/" className="btn btn-ghost btn-sm">Home</Link>
          </div>
        </div>
      </div>
    );
  }

  const pricing = booking.pricing || {};
  const tint = STATUS_TINT[booking.status] || "gold";
  const paymentSummary = summarizeBookingPayment({ ...booking, financial_summary: financialSummary || booking.financial_summary }, schedule);
  const visibility = getBookingDetailVisibility(user?.role);
  const isCustomer = ["customer", "corporate"].includes(user?.role);
  const isArtist = user?.role === "artist";
  const isAgency = user?.role === "agency";
  const isAdmin = user?.role === "admin";
  const isBookingCustomer = isCustomer && booking.customer_id === user?.id;
  const isBookingArtist = isArtist && booking.artist_id === user?.id;
  const isEventMarkedComplete = ["completed", "reviewed", "completed_by_artist"].includes(String(booking.status || "").toLowerCase());
  const canUploadEventMedia = isAdmin || ((isBookingCustomer || isBookingArtist) && isEventMarkedComplete);
  const isOperations = ["admin", "subadmin", "manager"].includes(user?.role);
  const artistDisplayName = (
    artistMeta?.stage_name ||
    artistMeta?.name ||
    [artistMeta?.first_name, artistMeta?.last_name].filter(Boolean).join(" ") ||
    booking?.artist_name ||
    booking?.artist_profile?.stage_name ||
    booking?.artist_profile?.name ||
    booking?.artist_id ||
    "Artist"
  );
  const customerDisplayName = booking.customer_name ||
    [customerUser?.first_name, customerUser?.last_name].filter(Boolean).join(" ") ||
    "Customer";
  const artistEmail = artistUser?.email || booking.artist_email || artistMeta?.email || "";
  const artistPhone = artistUser?.phone || booking.artist_phone || artistMeta?.phone || "";
  const customerEmail = booking.customer_email || customerUser?.email || "";
  const customerPhone = booking.customer_phone || customerUser?.phone || "";
  const attention = getBookingAttention(booking, user?.role);
  const progress = getBookingProgress(booking, paymentSummary, payouts, user?.role);
  const paidOut = payouts.reduce((sum, payout) => sum + Number(payout.amount || 0), 0);
  const artistContactVisible = canViewArtistContact(user?.role, contactUnlocked);
  const customerContactVisible = isOperations || isCustomer || (contactUnlocked && (isArtist || isAgency));
  const dashboardPath = {
    admin: "/admin",
    subadmin: "/admin",
    manager: "/manager",
    artist: "/artist",
    agency: "/agency",
    corporate: "/corporate",
    customer: "/customer",
  }[user?.role] || "/";
  const backPath = typeof location.state?.from === "string" && location.state.from.startsWith("/")
    ? location.state.from
    : dashboardPath;

  const saveEventDetails = async (event) => {
    event.preventDefault();
    setEventDetailsBusy(true);
    try {
      await api.patch(`/admin/bookings/${id}/event-details`, { details: eventDetailsDraft });
      toast("Event information saved.", "success");
      await loadEventMedia();
    } catch (error) {
      toast(fmt(error), "error");
    } finally {
      setEventDetailsBusy(false);
    }
  };

  const uploadFinished = async () => {
    setReplacingMediaId("");
    setEventMediaTitle("");
    toast("Event media uploaded.", "success");
    await loadEventMedia();
  };

  const deleteEventMedia = async (media) => {
    if (!window.confirm(`Delete "${media.title}" from this booking?`)) return;
    try {
      await api.delete(`/admin/bookings/${id}/event-media/${media.id}`);
      toast("Event media deleted.", "success");
      await loadEventMedia();
    } catch (error) {
      toast(fmt(error), "error");
    }
  };

  const renameEventMedia = async (media) => {
    const title = window.prompt("Event media title", media.title || "");
    if (title === null || !title.trim()) return;
    try {
      await api.patch(`/admin/bookings/${id}/event-media/${media.id}`, { title: title.trim() });
      toast("Event media title updated.", "success");
      await loadEventMedia();
    } catch (error) {
      toast(fmt(error), "error");
    }
  };

  const toggleEventMediaPublished = async (media) => {
    const published = !media.published;
    try {
      await api.patch(`/admin/bookings/${id}/event-media/${media.id}`, { published });
      toast(published ? "Media published to the Events Gallery." : "Media removed from the Events Gallery.", "success");
      await loadEventMedia();
    } catch (error) {
      toast(fmt(error), "error");
    }
  };

  return (
    <div>
      <Nav />
      <main className="booking-detail-page" data-testid="booking-detail">
        <Link to={backPath} className="bd-back-link" data-testid="bd-back">‹ Back to dashboard</Link>
        <header className="bd-page-head">
          <div>
            <div className="bd-ref-label">Booking reference</div>
            <h1 className="bd-page-ref" data-testid="bd-ref">
              {booking.ref || booking.id}
            </h1>
            <p className="bd-page-subtitle">
              {booking.event_type || "Event"} · {booking.event_date || "Date to be confirmed"}
              {booking.event_time ? ` at ${booking.event_time}` : ""}
            </p>
          </div>
          <div className="bd-page-status">
            <span className={`pill pill-${tint}`} data-testid="bd-status">{String(booking.status || "pending").replace(/_/g, " ")}</span>
            {booking.artist_payout_status && (
              <span
                className={`pill pill-${booking.artist_payout_status === "paid" ? "green" : "gold"}`}
                data-testid="bd-payout-status"
              >
                Payout: {booking.artist_payout_status}
              </span>
            )}
          </div>
        </header>

        <section className={`bd-attention ${attention.tone}`} role="status" data-testid="bd-attention">
          <div>
            <strong>{attention.title}</strong>
            <span>{attention.body}</span>
            {attention.actionHref && (
              <div style={{ marginTop: "var(--space-2)" }}>
                <Link to={attention.actionHref} className="btn btn-gold btn-sm" data-testid="bd-attention-action">
                  {attention.actionLabel}
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="bd-stepper-card" aria-labelledby="bd-timeline-title" data-testid="bd-progress">
          <div className="bd-stepper-head">
            <h2 className="bd-section-heading" id="bd-timeline-title">Booking timeline</h2>
            <p className="bd-section-note">
              {progress.filter((step) => step.done).length} of {progress.length} stages complete
            </p>
          </div>
          <ol className="bd-stepper">
            {progress.map((step) => (
              <li
                key={step.key}
                className={`bd-step${step.done ? " is-done" : ""}${step.current ? " is-current" : ""}`}
                aria-current={step.current ? "step" : undefined}
                data-testid={`bd-progress-${step.key}`}
              >
                <span className="bd-step-dot" aria-hidden="true">{step.done ? "✓" : ""}</span>
                <div className="bd-step-text">{step.label}</div>
                {(step.date || step.waiting || step.upcoming) && (
                  <div className="bd-step-meta">
                    {step.waiting ? "Action required" : step.upcoming ? "Upcoming" : String(step.date).slice(0, 10)}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>

        {/* ── Booking summary ───────────────────────────────────── */}
        <div className="bd-main-grid">
          <div className="bd-card-stack">
          <section className="bd-layout-card" data-testid="bd-summary-card">
            <h2 className="bd-section-heading">Event</h2>
            <dl className="bd-event-facts">
              <div className="bd-event-fact"><dt>Event type</dt><dd>{booking.event_type || "—"}</dd></div>
              <div className="bd-event-fact"><dt>Date and time</dt><dd>{booking.event_date || "—"}{booking.event_time ? `, ${booking.event_time}` : ""}</dd></div>
              <div className="bd-event-fact wide"><dt>Venue</dt><dd>{[booking.venue, booking.city].filter(Boolean).join(", ") || "—"}</dd></div>
              {booking.special_instructions && (
                <div className="bd-event-fact wide"><dt>Special instructions</dt><dd style={{ whiteSpace: "pre-wrap" }}>{booking.special_instructions}</dd></div>
              )}
            </dl>
            <div className="bd-people-grid">
              <section className="bd-person-card" data-testid="bd-artist-contact">
                <div className="bd-person-role">Artist</div>
                <div className="bd-person-name">{artistDisplayName}</div>
                {artistContactVisible ? (
                  <>
                    {artistEmail && <div className="bd-person-contact"><a href={`mailto:${artistEmail}`}>{artistEmail}</a></div>}
                    {artistPhone && <div className="bd-person-contact"><a href={`tel:${artistPhone}`}>{artistPhone}</a></div>}
                  </>
                ) : (
                  <div className="bd-person-locked">Contact details are available after the booking payment is made.</div>
                )}
              </section>
              {shouldShowCustomerContactCard(user?.role) && (
                <section className="bd-person-card" data-testid="bd-customer-contact">
                  <div className="bd-person-role">Customer</div>
                  <div className="bd-person-name">{customerDisplayName}</div>
                  {customerContactVisible ? (
                    <>
                      {customerEmail && <div className="bd-person-contact"><a href={`mailto:${customerEmail}`}>{customerEmail}</a></div>}
                      {customerPhone && <div className="bd-person-contact"><a href={`tel:${customerPhone}`}>{customerPhone}</a></div>}
                    </>
                  ) : (
                    <div className="bd-person-locked">Contact details are available after the booking payment is made.</div>
                  )}
                </section>
              )}
            </div>
            {booking.tech_rider_snapshot?.mime === "application/pdf" && (
              <section className="bd-other-information" data-testid="bd-tech-rider-snapshot">
                <div>
                  <div className="bd-person-role">Other information</div>
                  <h3 className="bd-section-heading">Artist Tech Rider</h3>
                  <p className="text-muted fs-12">
                    The technical requirements attached to this booking.
                  </p>
                  <div className="text-muted fs-11 mt-4">
                    Booking version · {String(booking.tech_rider_snapshot.uploaded_at || booking.tech_rider_snapshot.attached_at || "").slice(0, 10)}
                  </div>
                </div>
                <TechRiderLink
                  bookingId={id}
                  filename={booking.tech_rider_snapshot.filename || "Tech Rider PDF"}
                  label="View Tech Rider PDF"
                  testId="bd-tech-rider-download"
                />
              </section>
            )}
          </section>
          </div>

          <aside className="bd-layout-card bd-payment-aside" data-testid="bd-pricing-card">
            <div className="bd-stepper-head">
              <h2 className="bd-section-heading">Payment summary</h2>
              <span className={`pill pill-${tint}`} style={{ padding: "var(--space-1) 8px", fontSize: 10 }}>
                {paymentSummary.status}
              </span>
            </div>

            <div className="bd-payment-kpis">
              {isArtist ? (
                <>
                  <PaymentKpi label="Artist payable" value={paymentSummary.artistPayableTotal} accent />
                  <PaymentKpi label="Paid out" value={paymentSummary.artistPayableAccrued} />
                  <PaymentKpi label="Remaining" value={paymentSummary.artistPending} />
                </>
              ) : (
                <>
                  <PaymentKpi label={isCustomer ? "Total" : "Customer total"} value={paymentSummary.total} accent />
                  <PaymentKpi label={isCustomer ? "Paid" : "Customer paid"} value={paymentSummary.paid} />
                  <PaymentKpi label={isCustomer ? "Due" : "Customer due"} value={paymentSummary.due} />
                  {visibility.showArtistPayoutSummary && (
                    <>
                      <PaymentKpi label="Artist payable" value={paymentSummary.artistPayableTotal} accent />
                      {isAgency && <PaymentKpi label="Paid to artist" value={paidOut} />}
                    </>
                  )}
                </>
              )}
            </div>

            {!isArtist && paymentSummary.total > 0 && (
              <>
                <div className="bd-payment-bar" role="progressbar" aria-valuemin="0" aria-valuemax={paymentSummary.total} aria-valuenow={paymentSummary.paid} aria-label="Amount paid">
                  <span style={{ width: `${Math.min(100, Math.max(0, paymentSummary.paid / paymentSummary.total * 100))}%` }} />
                </div>
                <p className="bd-section-note" style={{ marginTop: "var(--space-2)" }}>
                  ₹{money(paymentSummary.paid)} of ₹{money(paymentSummary.total)} paid
                </p>
              </>
            )}

            {paymentSummary.nextMilestone && (isCustomer || isOperations || isAgency) && (
              <div className="bd-payment-next">
                <strong>Next: {paymentSummary.nextMilestone.label} · ₹{money(paymentSummary.nextMilestone.amount)}</strong>
                <span>{formatPaymentDueLabel(paymentSummary.nextMilestone, booking.event_date)}</span>
              </div>
            )}

            {visibility.showInternalPaymentBreakdown && (
              <dl className="bd-split-list" data-testid="bd-internal-payment-breakdown">
                <dt>Service cost</dt><dd>₹{money(paymentSummary.customerBillValue)}</dd>
                <dt>GST</dt><dd>₹{money(paymentSummary.gstValue)}</dd>
                <dt>Customer total</dt><dd>₹{money(paymentSummary.total)}</dd>
                <dt>BookTalent share</dt><dd>₹{money(paymentSummary.booktalentShare)}</dd>
                <dt>Artist payable</dt><dd>₹{money(paymentSummary.artistPayableTotal)}</dd>
              </dl>
            )}

            {isAgency && schedule?.milestones?.length > 0 && (
              <div style={{ marginTop: "var(--space-4)" }}>
                <h3 className="bd-section-heading" style={{ fontSize: 14 }}>Payment installments</h3>
                <div className="bd-installment-list">
                  {schedule.milestones.map((milestone, index) => (
                    <div className="bd-installment" key={`${milestone.label}-${index}`}>
                      <div>
                        <div className="bd-installment-name">{formatPaymentMilestoneLabel(milestone, index, booking.event_date)}</div>
                        <div className="bd-installment-meta">{formatPaymentDueLabel(milestone)}</div>
                      </div>
                      <div className="bd-installment-amount">
                        ₹{money(milestone.amount)}
                        <span className="bd-installment-payout">{milestone.status || "pending"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isArtist && payouts.length > 0 && (
              <div style={{ marginTop: "var(--space-4)" }}>
                <h3 className="bd-section-heading" style={{ fontSize: 14 }}>Payouts received</h3>
                <div className="bd-installment-list">
                  {payouts.map((payout) => (
                    <div className="bd-installment" key={payout.id}>
                      <div>
                        <div className="bd-installment-name">{payout.paid_on || String(payout.created_at || "").slice(0, 10)}</div>
                        <div className="bd-installment-meta">{String(payout.method || "Payout").toUpperCase()}</div>
                      </div>
                      <div className="bd-installment-amount">₹{money(payout.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canAccessEventMedia && (
              <a className="bd-event-media-shortcut" href="#bd-event-media" data-testid="bd-event-media-shortcut">
                Event information &amp; media
                {eventMedia.media.length > 0 && <span>{eventMedia.media.length}</span>}
              </a>
            )}
          </aside>

        {canAccessEventMedia && <section id="bd-event-media" className="card card-pad bd-layout-card bd-event-media" aria-label="Event information and media" data-testid="bd-event-media">
          <div className="bd-event-media-head">
            <div>
              <h2>Event information &amp; media</h2>
              <p>Shared with the customer and artist for this booking.</p>
            </div>
            {eventMedia.media.length > 0 && (
              <span className="bd-event-media-count">
                {eventMedia.media.length} {eventMedia.media.length === 1 ? "file" : "files"}
              </span>
            )}
          </div>

          {isAdmin ? (
            <form className="bd-event-details-editor" onSubmit={saveEventDetails}>
              <label htmlFor="bd-event-details">Event information</label>
              <textarea
                id="bd-event-details"
                value={eventDetailsDraft}
                onChange={(event) => setEventDetailsDraft(event.target.value)}
                maxLength={5000}
                rows={4}
                placeholder="Add event notes, schedule details, or other information for the customer and artist."
                data-testid="bd-event-details-input"
              />
              <div className="bd-event-details-actions">
                <span>{eventDetailsDraft.length}/5000</span>
                <button type="submit" className="btn btn-gold btn-sm" disabled={eventDetailsBusy}>
                  {eventDetailsBusy ? "Saving…" : "Save event information"}
                </button>
              </div>
            </form>
          ) : (
            <div className="bd-event-details" data-testid="bd-event-details">
              {eventMedia.details
                ? <p>{eventMedia.details}</p>
                : <p className="text-muted">No additional event information has been added.</p>}
            </div>
          )}

          {canUploadEventMedia && (
            <div className="bd-event-media-upload">
              <h3>Upload event photos or videos</h3>
              <p>
                {isAdmin
                  ? "Uploads stay private to this booking until you publish them to the public Events Gallery."
                  : "Your uploads will be private to this booking until an admin publishes them to the Events Gallery."}
              </p>
              <label htmlFor="bd-event-media-title">File title <span>(optional)</span></label>
              <input
                id="bd-event-media-title"
                className="field-input"
                value={eventMediaTitle}
                maxLength={200}
                onChange={(event) => setEventMediaTitle(event.target.value)}
                placeholder="For example: Stage setup or Event highlights"
                data-testid="bd-event-media-title"
              />
              <MediaUploader
                type="booking_event"
                accept="image/*,video/*"
                maxFiles={5}
                bookingId={id}
                title={eventMediaTitle.trim() || undefined}
                onComplete={uploadFinished}
              />
            </div>
          )}
          {(isBookingCustomer || isBookingArtist) && !isEventMarkedComplete && (
            <p className="bd-event-media-upload-notice" data-testid="bd-event-media-upload-locked">
              You can upload event photos or videos here once the event is marked complete by the artist or admin.
            </p>
          )}

          {eventMedia.media.length > 0 ? (
            <div className="bd-event-media-grid">
              {eventMedia.media.map((media) => {
                const isVideo = media.mime?.startsWith("video/");
                const isImage = media.mime?.startsWith("image/");
                const fileUrl = `${API}/media/${encodeURIComponent(media.id)}/file`;
                return (
                  <article className="bd-event-media-item" key={media.id} data-testid={`bd-event-media-${media.id}`}>
                    <div className="bd-event-media-preview">
                      {isVideo ? (
                        <video src={fileUrl} controls preload="metadata" aria-label={media.title} />
                      ) : isImage ? (
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                          <img src={`${API}/media/${encodeURIComponent(media.id)}/thumb`} alt={media.title} loading="lazy" />
                        </a>
                      ) : (
                        <a className="bd-event-media-download" href={fileUrl} target="_blank" rel="noopener noreferrer">
                          Open file
                        </a>
                      )}
                    </div>
                    <div className="bd-event-media-item-info">
                      <strong title={media.title}>{media.title}</strong>
                      <span>{isVideo ? "Video" : isImage ? "Photo" : "File"}</span>
                    </div>
                    {isAdmin && (
                      <div className="bd-event-media-actions">
                        <button
                          type="button"
                          className={`btn btn-xs ${media.published ? "btn-gold" : "btn-ghost"}`}
                          onClick={() => toggleEventMediaPublished(media)}
                          title={media.published ? "Visible in the public Events Gallery" : "Private until published"}
                        >
                          {media.published ? "Published" : "Publish"}
                        </button>
                        <button type="button" className="btn btn-ghost btn-xs" onClick={() => renameEventMedia(media)}>
                          Edit title
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => setReplacingMediaId(replacingMediaId === media.id ? "" : media.id)}
                        >
                          {replacingMediaId === media.id ? "Cancel replace" : "Replace"}
                        </button>
                        <button type="button" className="btn btn-red btn-xs" onClick={() => deleteEventMedia(media)}>
                          Delete
                        </button>
                      </div>
                    )}
                    {isAdmin && replacingMediaId === media.id && (
                      <div className="bd-event-media-replace">
                        <p>Replacing a published file keeps it published; private files remain private.</p>
                        <MediaUploader
                          key={`replace-${media.id}`}
                          type="booking_event"
                          accept="image/*,video/*"
                          maxFiles={1}
                          bookingId={id}
                          replaceMediaId={media.id}
                          title={media.title}
                          onComplete={uploadFinished}
                        />
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="bd-event-media-empty" data-testid="bd-event-media-empty">
              No event photos or videos have been added yet.
            </div>
          )}
        </section>}
        </div>

        {visibility.showCustomerPaymentSchedule && <CustomerPaymentSchedule schedule={schedule} bookingId={id} eventDate={booking.event_date} onScheduleChange={setSchedule} />}

        {/* ── Mutual Refund Panel — customer or artist can request ── */}
        {visibility.showMutualRefund && (
          <MutualRefundPanel booking={booking} user={user} />
        )}

        {/* ── Payment Timeline ──────────────────────────────────── */}
        {visibility.showPaymentTimeline && (
          <div className="mb-16" data-testid="bd-payment-timeline">
            <PaymentTimeline bookingId={id} canEdit={canEditPayments} initialSchedule={schedule} />
          </div>
        )}

        {/* ── Payout Ledger (admin/manager/agency/artist only) ──── */}
        {visibility.showPayoutLedger && payouts.length > 0 && (
          <div className="card card-pad mb-16" data-testid="bd-payout-ledger">
            <h3 className="fw-700 mb-8">Payout Ledger</h3>
            <table className="table w-full fs-13">
              <thead>
                <tr><th>Date</th><th>Amount</th><th>Method</th><th>UTR</th><th>By</th><th>Proof</th></tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} data-testid={`bd-payout-${p.id}`}>
                    <td>{(p.paid_on || p.created_at || "").slice(0, 10)}</td>
                    <td className="text-gold">{money(p.amount)}</td>
                    <td>{(p.method || "").toUpperCase()}</td>
                    <td className="font-mono fs-11">{p.utr}</td>
                    <td className="text-muted fs-11">{p.paid_by_email || "—"}</td>
                    <td>
                      {p.proof_document?.url ? (
                        <a href={`/api${p.proof_document.url}`} target="_blank" rel="noopener noreferrer">
                          {p.proof_document.filename || "View proof"}
                        </a>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </main>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// BookingTimeline — visual audit trail of every important stage.
// Derives its stages from the booking document + payout ledger so it works
// without a dedicated status_history collection.
// ────────────────────────────────────────────────────────────────────────
function PaymentKpi({ label, value, accent = false }) {
  return (
    <div className="bd-payment-kpi">
      <div className="bd-payment-kpi-label">{label}</div>
      <div className={`bd-payment-kpi-value${accent ? " accent" : ""}`}>₹{money(value)}</div>
    </div>
  );
}

function CustomerPaymentSchedule({ schedule, bookingId, eventDate, onScheduleChange }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [scope, setScope] = useState("next");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const paymentAttempts = schedule?.payment_attempts || [];
  const hasPendingAttempt = paymentAttempts.some((attempt) => (attempt.status || "").toLowerCase() === "pending");
  const hasDelayedAttempt = paymentAttempts.some((attempt) => getPaymentAttemptDisplayState(attempt).isDelayed);
  const unpaidIndex = schedule?.milestones?.findIndex((milestone) => !["paid", "completed", "confirmed"].includes((milestone.status || "pending").toLowerCase())) ?? -1;

  useEffect(() => {
    if (!hasPendingAttempt) return undefined;
    let active = true;
    let polls = 0;
    let timer;
    const poll = async () => {
      polls += 1;
      try {
        const response = await api.get(`/bookings/${bookingId}/schedule`);
        if (!active) return;
        onScheduleChange(response.data);
        const stillPending = (response.data?.payment_attempts || []).some(
          (attempt) => (attempt.status || "").toLowerCase() === "pending",
        );
        if (stillPending) timer = setTimeout(poll, polls < 10 ? 3000 : polls < 22 ? 10000 : 30000);
      } catch {
        if (active) timer = setTimeout(poll, polls < 10 ? 3000 : polls < 22 ? 10000 : 30000);
      }
    };
    timer = setTimeout(poll, 3000);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [bookingId, hasPendingAttempt, onScheduleChange]);

  if (!schedule?.milestones?.length) return null;

  const startPayment = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await api.post("/payments/easebuzz/init", {
        booking_ids: [bookingId],
        payment_scope: scope,
        method: "hosted",
      });
      if (!response.data?.payment_url) throw new Error("Payment link was not created");
      window.location.assign(response.data.payment_url);
    } catch (e) {
      setError(fmt(e));
      setBusy(false);
    }
  };

  return (
    <div id="bd-payment-schedule" className="card card-pad mb-16" data-testid="bd-customer-payment-schedule">
      <h3 className="fw-700 mb-8">Payment Schedule</h3>
      {paymentAttempts.length > 0 && (
        <div className="mb-16" data-testid="bd-payment-activity">
          <div className="fs-11 text-muted mb-8">Recent payment activity</div>
          {paymentAttempts.map((attempt, index) => {
            const status = (attempt.status || "pending").toLowerCase();
            const state = getPaymentAttemptDisplayState(attempt);
            const timestamp = getPaymentAttemptTimestamp(attempt);
            return (
              <div key={`${attempt.created_at || "attempt"}-${index}`} className="flex-between fs-12 mb-8" style={{ gap: "var(--space-3)" }} data-testid={`bd-payment-attempt-${status}`}>
                <span>
                  <span className="fw-700">{attempt.label || "Payment attempt"}</span>
                  {timestamp ? <span className="text-muted"> · {timestamp.label}: {timestamp.value}</span> : null}
                  <span className="text-muted"> · ₹{money(attempt.amount)}</span>
                </span>
                <span className={`pill pill-${state.tint}`} style={{ padding: "var(--space-1) 8px", fontSize: 10 }}>{state.label}</span>
              </div>
            );
          })}
          {hasDelayedAttempt && (
            <div className="text-muted fs-12 mt-8" role="status" data-testid="bd-payment-confirmation-delayed">
              The payment provider has not confirmed this attempt yet. We are still checking. Please wait for a final status before trying again.
            </div>
          )}
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {schedule.milestones.map((milestone, idx) => (
          <li key={`${milestone.label}-${idx}`} className="flex-between fs-13 mb-8" style={{ gap: "var(--space-3)", flexWrap: "wrap" }}>
            <span>
              <span className="fw-700">{formatPaymentMilestoneLabel(milestone, idx, eventDate)}</span>
              <span className="text-muted fs-11" style={{ display: "block" }}>{formatPaymentDueLabel(milestone, eventDate)}</span>
            </span>
            <span className="text-gold fw-700">₹{money(milestone.amount || 0)}</span>
            <span className="flex items-center gap-8" style={{ marginLeft: "auto" }}>
              <span className={`pill pill-${["paid", "completed", "confirmed"].includes((milestone.status || "pending").toLowerCase()) ? "green" : "gold"}`} style={{ padding: "var(--space-1) 8px", fontSize: 10 }}>
                {["paid", "completed", "confirmed"].includes((milestone.status || "pending").toLowerCase()) ? "Paid" : "Pending"}
              </span>
              {idx === unpaidIndex && (
                openIndex === idx ? (
                  <span className="flex items-center gap-8">
                    <select className="field-input" value={scope} onChange={(e) => setScope(e.target.value)} style={{ width: 190, padding: "var(--space-1) 8px", fontSize: 12 }} data-testid="payment-scope-select">
                      <option value="next">Pay this installment</option>
                      <option value="remaining">Pay full remaining balance</option>
                    </select>
                    <button type="button" className="btn btn-gold btn-xs" onClick={startPayment} disabled={busy} data-testid="confirm-installment-payment">
                      {busy ? "Opening…" : "Continue to Pay"}
                    </button>
                  </span>
                ) : (
                  <button type="button" className="btn btn-gold btn-xs" onClick={() => { setOpenIndex(idx); setScope("next"); setError(""); }} data-testid={`pay-installment-${idx}`}>
                    Pay Amount
                  </button>
                )
              )}
            </span>
            {idx > unpaidIndex && unpaidIndex >= 0 && (
              <span className="text-muted fs-11" style={{ width: "100%", textAlign: "right" }}>Pay the previous installment first</span>
            )}
          </li>
        ))}
      </ul>
      {error && <div className="text-red fs-12 mt-8" data-testid="installment-payment-error">{error}</div>}
    </div>
  );
}

function ArtistPayoutSchedule({ schedule }) {
  if (!schedule?.milestones?.length) return null;

  return (
    <div className="card card-pad mb-16" data-testid="bd-artist-payout-schedule">
      <h3 className="fw-700 mb-8">Artist Payout Schedule</h3>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {schedule.milestones.map((milestone, idx) => (
          <li key={`${milestone.label}-${idx}`} className="flex-between fs-13 mb-8" style={{ gap: "var(--space-3)" }}>
            <span>
              <span className="fw-700">{formatPaymentMilestoneLabel(milestone, idx)}</span>
              {` · ${formatPaymentDueLabel(milestone)}`}
            </span>
            <span className="text-gold fw-700">₹{money(milestone.amount || 0)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ArtistTimeline({ booking, payouts }) {
  const [realEvents, setRealEvents] = useState([]);
  useEffect(() => {
    if (!booking?.id) return;
    api.get(`/bookings/${booking.id}/timeline`)
      .then((r) => setRealEvents(r.data?.items || []))
      .catch(() => setRealEvents([]));
  }, [booking?.id]);

  if (!booking) return null;

  const evtDate = booking.event_date || "";
  const today = new Date().toISOString().slice(0, 10);
  const evtInFuture = evtDate && evtDate >= today;
  const stages = [
    { key: "artist_selected", label: "Artist Selected", at: booking.created_at, done: !!booking.artist_id },
    { key: "booking_confirmation", label: "Booking Confirmation", at: booking.confirmed_at, done: ["confirmed", "started", "completed", "reviewed"].includes(booking.status), hint: booking.status === "pending_artist" ? "Action required" : "" },
    { key: "booking_confirmed", label: "Booking Confirmed", at: booking.confirmed_at, done: ["confirmed", "started", "completed", "reviewed"].includes(booking.status) },
    { key: "event", label: `Event · ${evtDate || "TBD"}`, at: evtDate, done: evtDate && !evtInFuture, hint: evtInFuture ? "Upcoming" : "" },
    { key: "artist_payout", label: "Artist Payout", at: (payouts && payouts[0]?.paid_on) || null, done: (payouts || []).length > 0 },
    { key: "completed", label: "Completed", at: booking.completed_at, done: booking.status === "completed" || booking.status === "reviewed" },
  ];

  const evByKind = {};
  (realEvents || []).forEach((ev) => {
    const map = {
      artist_selected: "artist_selected",
      booking_confirmed: "booking_confirmed",
      booking_confirmation: "booking_confirmation",
      event_completed: "event",
      artist_payout: "artist_payout",
      completed: "completed",
    };
    const k = map[ev.kind] || ev.kind;
    if (!evByKind[k]) evByKind[k] = ev;
  });
  stages.forEach((s) => {
    const ev = evByKind[s.key];
    if (ev && ev.at) {
      s.at = ev.at;
      s.done = true;
    }
  });

  const currentIndex = (() => {
    for (let i = stages.length - 1; i >= 0; i--) {
      if (stages[i].done) return i + 1;
    }
    return 0;
  })();

  return (
    <div className="card card-pad mb-16" data-testid="bd-artist-timeline">
      <div className="flex-between mb-12">
        <h3 className="fw-700">Booking Timeline</h3>
        <span className="text-muted fs-11">{currentIndex} of {stages.length} stages</span>
      </div>
      <div style={{ position: "relative", paddingLeft: 22 }}>
        <div style={{ position: "absolute", top: 6, bottom: 6, left: 10, width: 2, background: "var(--glass-border)" }} />
        {stages.map((s, i) => {
          const isCurrent = i === currentIndex - 1;
          return (
            <div key={s.key} style={{ position: "relative", marginBottom: "var(--space-3)", minHeight: 22 }}>
              <div style={{ position: "absolute", left: -18, top: 3, width: 18, height: 18, borderRadius: "50%", background: s.done ? "var(--green)" : isCurrent ? "var(--gold)" : "var(--bg3)", color: (s.done || isCurrent) ? "#fff" : "var(--white-muted)", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", border: isCurrent ? "3px solid var(--gold-border)" : "2px solid var(--glass-border)" }}>{s.done ? "✓" : ""}</div>
              <div style={{ fontSize: 13, fontWeight: isCurrent || s.done ? 700 : 500, color: s.done ? "var(--white)" : isCurrent ? "var(--gold-light)" : "var(--white-muted)" }}>{s.label}</div>
              {s.at && <div className="text-muted fs-11" style={{ marginTop: "var(--space-1)" }}>{String(s.at).slice(0, 10)}</div>}
              {s.hint && <div className="text-muted fs-11" style={{ marginTop: "var(--space-1)", fontStyle: "italic" }}>{s.hint}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BookingTimeline({ booking, payouts, customerView = false }) {
  const [realEvents, setRealEvents] = useState([]);
  useEffect(() => {
    if (!booking?.id) return;
    api.get(`/bookings/${booking.id}/timeline`)
      .then((r) => setRealEvents(r.data?.items || []))
      .catch(() => setRealEvents([]));
  }, [booking?.id]);
  if (!booking) return null;
  const p = booking.pricing || {};
  const total = Number(p.total || 0);
  const paid = Number(booking.paid_amount || 0);
  const totalPayout = (payouts || []).reduce((s, x) => s + Number(x.amount || 0), 0);
  const evtDate = booking.event_date || "";
  const today = new Date().toISOString().slice(0, 10);
  const evtInFuture = evtDate && evtDate >= today;

  const stages = customerView ? [
    {
      key: "lead_created",
      label: "Booking Created",
      at: booking.created_at,
      done: !!booking.created_at,
    },
    {
      key: "artist_selected",
      label: "Artist Selected",
      at: booking.created_at,
      done: !!booking.artist_id,
    },
    {
      key: "booking_confirmed",
      label: "Booking Confirmed",
      at: booking.confirmed_at,
      done: ["confirmed", "started", "completed", "reviewed"].includes(booking.status),
      hint: booking.status === "pending_artist" ? "Awaiting confirmation" : "",
    },
    {
      key: "payment_received",
      label: paid > 0 ? `Payment Received · ₹${money(paid)}` : "Payment Received",
      at: booking.first_payment_at,
      done: paid > 0,
    },
    {
      key: "event",
      label: `Event${evtDate ? ` · ${evtDate}` : ""}`,
      at: evtDate,
      done: evtDate && !evtInFuture,
      hint: evtInFuture ? "Upcoming" : "",
    },
    {
      key: "completed",
      label: "Completed",
      at: booking.completed_at,
      done: booking.status === "completed" || booking.status === "reviewed",
    },
  ] : [
    {
      key: "lead_created",
      label: "Lead / Booking Created",
      at: booking.created_at,
      done: !!booking.created_at,
    },
    {
      key: "manager_assigned",
      label: "Manager Assigned",
      at: booking.manager_assigned_at,
      done: !!booking.assigned_manager_id,
      hint: booking.assigned_manager_id ? "Assigned" : "Auto-assigns when a lead qualifies",
    },
    {
      key: "artist_selected",
      label: "Artist Selected",
      at: booking.created_at,
      done: !!booking.artist_id,
    },
    {
      key: "booking_confirmed",
      label: "Booking Confirmed",
      at: booking.confirmed_at,
      done: ["confirmed", "started", "completed", "reviewed"].includes(booking.status),
      hint: booking.status === "pending_artist" ? "Awaiting artist confirmation" : "",
    },
    {
      key: "payment_received",
      label: paid > 0 ? `Payment Received · ₹${money(paid)} of ₹${money(total)}` : "Payment Received",
      at: booking.first_payment_at,
      done: paid > 0,
    },
    {
      key: "artist_payout",
      label: totalPayout > 0
        ? `Artist Payout · ₹${money(totalPayout)}`
        : "Artist Payout",
      at: (payouts && payouts[0]?.paid_on) || null,
      done: totalPayout > 0,
      hint: totalPayout === 0 && paid > 0 ? "Pending — mark paid from payout ledger" : "",
    },
    {
      key: "remaining_payment",
      label: total > paid
        ? `Remaining Payment · ₹${money(total - paid)} due`
        : "Remaining Payment · settled",
      at: booking.remaining_paid_at,
      done: total > 0 && paid >= total,
    },
    {
      key: "event",
      label: `Event${evtDate ? ` · ${evtDate}` : ""}`,
      at: evtDate,
      done: evtDate && !evtInFuture,
      hint: evtInFuture ? "Upcoming" : "",
    },
    {
      key: "final_payment",
      label: "Final Payment / Settlement",
      at: booking.finalized_at,
      done: booking.status === "completed" || booking.status === "reviewed",
    },
    {
      key: "completed",
      label: "Completed",
      at: booking.completed_at,
      done: booking.status === "completed" || booking.status === "reviewed",
    },
  ];

  // Overlay real timestamps from booking_events when present so displayed
  // dates reflect actual DB events rather than derived ones.
  const evByKind = {};
  (realEvents || []).forEach((ev) => {
    // Normalise a couple of aliases from server-side event kinds.
    const map = {
      created: "lead_created",
      manager_assigned: "manager_assigned",
      artist_confirmed: "booking_confirmed",
      payment_received: "payment_received",
      artist_payout: "artist_payout",
      event_completed: "event",
      completed: "completed",
    };
    const k = map[ev.kind] || ev.kind;
    if (!evByKind[k]) evByKind[k] = ev;
  });
  stages.forEach((s) => {
    const ev = evByKind[s.key];
    if (ev && ev.at) {
      s.at = ev.at;
      s.done = true;
    }
  });

  const currentIndex = (() => {
    for (let i = stages.length - 1; i >= 0; i--) {
      if (stages[i].done) return i + 1;
    }
    return 0;
  })();

  return (
    <div className="card card-pad mb-16" data-testid="bd-timeline">
      <div className="flex-between mb-12">
        <h3 className="fw-700">Booking Timeline</h3>
        <span className="text-muted fs-11">{currentIndex} of {stages.length} stages</span>
      </div>
      <div style={{ position: "relative", paddingLeft: 22 }}>
        <div style={{
          position: "absolute", top: 6, bottom: 6, left: 10, width: 2,
          background: "var(--bg3)",
        }} />
        {stages.map((s, i) => {
          const isCurrent = i === currentIndex - 1;
          return (
            <div
              key={s.key}
              data-testid={`bd-timeline-${s.key}`}
              style={{ position: "relative", marginBottom: "var(--space-3)", minHeight: 22 }}
            >
              <div style={{
                position: "absolute", left: -18, top: 3, width: 18, height: 18,
                borderRadius: "50%",
                background: s.done ? "var(--green)" : isCurrent ? "var(--gold)" : "var(--bg3)",
                color: (s.done || isCurrent) ? "#fff" : "var(--white-muted)",
                fontSize: 10, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: isCurrent ? "3px solid var(--gold-border)" : "2px solid var(--glass-border)",
              }}>{s.done ? "✓" : ""}</div>
              <div style={{
                fontSize: 13, fontWeight: isCurrent || s.done ? 700 : 500,
                color: s.done ? "var(--white)"
                        : isCurrent ? "var(--gold-light)"
                        : "var(--white-muted)",
              }}>{s.label}</div>
              {s.at && (
                <div className="text-muted fs-11" style={{ marginTop: "var(--space-1)" }}>
                  {String(s.at).slice(0, 10)}
                </div>
              )}
              {s.hint && (
                <div className="text-muted fs-11" style={{ marginTop: "var(--space-1)", fontStyle: "italic" }}>
                  {s.hint}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// MutualRefundPanel — customer or artist can raise a refund request; the
// other party accepts/rejects. On accept, the booking is flagged and (if
// enabled) the payout provider dispatches the actual refund.
// ────────────────────────────────────────────────────────────────────────
function MutualRefundPanel({ booking, user }) {
  const toast = useToast();
  const [req, setReq] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const paid = Number(booking.paid_amount || 0);
  const reload = () => api.get(`/bookings/${booking.id}/refund-status`)
    .then((r) => setReq(r.data?.request || null))
    .catch(() => setReq(null));
  useEffect(() => { reload(); }, [booking.id]);

  const submit = async () => {
    const amt = amount ? parseFloat(amount) : paid;
    if (!amt || amt <= 0) { toast("Enter a positive refund amount", "error"); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/refund-request`, { amount: amt, reason });
      toast("Refund request sent — awaiting counter party's acknowledgement", "success");
      setAmount(""); setReason("");
      reload();
    } catch (e) { toast(fmt(e), "error"); }
    setBusy(false);
  };

  const respond = async (accept) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/refund-${accept ? "accept" : "reject"}`);
      toast(accept ? "Refund accepted — payout in progress" : "Refund request rejected", "success");
      reload();
    } catch (e) { toast(fmt(e), "error"); }
    setBusy(false);
  };

  const iAmCounter = req && req.counter_id === user?.id && req.status === "pending_counter_ack";
  const iAmRequester = req && req.requested_by_id === user?.id;

  return (
    <div className="card card-pad mb-16" data-testid="bd-refund-panel">
      <h3 className="fw-700 mb-8">Mutual Refund</h3>

      {req && req.status === "pending_counter_ack" && (
        <div style={{
          background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.3)",
          borderRadius: 8, padding: "var(--space-3)", marginBottom: "var(--space-3)",
        }} data-testid="bd-refund-pending">
          <div className="fw-700 fs-14">₹{money(req.amount)} refund awaiting {req.counter_role} acknowledgement</div>
          {req.reason && <div className="text-muted fs-12 mt-4">Reason: {req.reason}</div>}
          {iAmCounter && (
            <div className="flex gap-8 mt-8">
              <button className="btn btn-gold btn-sm" onClick={() => respond(true)} disabled={busy} data-testid="bd-refund-accept">Accept</button>
              <button className="btn btn-ghost btn-sm" onClick={() => respond(false)} disabled={busy} data-testid="bd-refund-reject">Reject</button>
            </div>
          )}
          {iAmRequester && <div className="text-muted fs-12 mt-8">Waiting on the other party…</div>}
        </div>
      )}

      {req && req.status === "accepted" && (
        <div className="text-good fs-13 mb-8" data-testid="bd-refund-accepted">
           Refund of ₹{money(req.amount)} accepted on {String(req.counter_accepted_at || "").slice(0, 10)}.
        </div>
      )}

      {req && req.status === "rejected" && (
        <div className="fs-13 mb-8" style={{ color: "#e57373" }} data-testid="bd-refund-rejected">
          Refund was rejected. You can raise a new request if circumstances change.
        </div>
      )}

      {(!req || req.status !== "pending_counter_ack") && paid > 0 && (
        <div style={{ display: "grid", gap: "var(--space-2)" }}>
          <div className="text-muted fs-13">
            If both parties agree, either of you can start a mutual refund. The counter party must accept before it's processed.
          </div>
          <div className="grid grid-2 gap-8">
            <input
              type="number"
              min="0"
              className="input"
              placeholder={`Amount (max ₹${money(paid)})`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="bd-refund-amount"
            />
            <input
              className="input"
              placeholder="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="bd-refund-reason"
            />
          </div>
          <div>
            <button className="btn btn-gold btn-sm" onClick={submit} disabled={busy} data-testid="bd-refund-request">
              {busy ? "Sending…" : "Request Refund"}
            </button>
          </div>
        </div>
      )}

      {(!req || req.status !== "pending_counter_ack") && paid === 0 && (
        <div className="text-muted fs-13">
          No payment has been received against this booking yet — refunds unavailable.
        </div>
      )}
    </div>
  );
}
